import { create } from 'zustand';

import { clientApi, deballer } from '@/shared/api/client';
import { ecrireJetons, viderJetons } from '@/shared/api/jetons';

interface ReponseSession {
  jetonAcces: string;
  jetonRafraichissement: string;
  expireLe: string;
  utilisateurId: string;
  entrepriseActive: string | null;
  role: string | null;
  estAdministrateurPlateforme: boolean;
}

export interface EtatSession {
  utilisateurId: string | null;
  email: string | null;

  /**
   * Acces d'administration, TEL QUE L'API L'AFFIRME.
   *
   * Ce drapeau vient de la reponse du serveur, pas d'un decodage du JWT cote client.
   * La distinction n'est pas theorique : decoder le jeton donnerait l'illusion que le
   * client arbitre, alors qu'il ne fait que relayer. La verite reste l'API — qui refuse
   * de toute facon chaque route d'administration a un compte non habilite.
   *
   * `null` signifie « pas encore etabli » et se distingue de `false` : on ne montre ni
   * l'application ni le refus tant qu'on ne sait pas.
   */
  estAdministrateur: boolean | null;

  connexionEnCours: boolean;

  connecter: (email: string, motDePasse: string) => Promise<void>;
  deconnecter: () => Promise<void>;
}

/**
 * Session de l'administration de plateforme.
 *
 * DEUX DIFFERENCES DE FOND AVEC LA SESSION DE L'APP UTILISATEUR :
 *
 * 1. **Pas de restauration au demarrage.** Aucun jeton n'est persiste (voir `jetons.ts`),
 *    il n'y a donc rien a restaurer : chaque ouverture de l'outil demande une connexion.
 * 2. **La connexion peut reussir et l'acces etre refuse.** Un compte valide mais non
 *    administrateur obtient une session parfaitement legitime — pour l'app utilisateur.
 *    Ici, elle ne donne droit a rien. On la referme immediatement plutot que de garder
 *    en memoire des jetons dont cette application n'a aucun usage.
 */
export const useSession = create<EtatSession>()((set) => ({
  utilisateurId: null,
  email: null,
  estAdministrateur: null,
  connexionEnCours: false,

  async connecter(email, motDePasse) {
    set({ connexionEnCours: true });

    try {
      const session = (await deballer(
        clientApi.POST('/v1/auth/connexion', { body: { email, motDePasse } }),
      )) as ReponseSession;

      if (!session.estAdministrateurPlateforme) {
        // Compte valide, acces refuse. On ne conserve rien : ni jeton, ni identite.
        viderJetons();
        set({
          utilisateurId: null,
          email: null,
          estAdministrateur: false,
          connexionEnCours: false,
        });
        return;
      }

      ecrireJetons(session.jetonAcces, session.jetonRafraichissement, session.expireLe);

      set({
        utilisateurId: session.utilisateurId,
        email,
        estAdministrateur: true,
        connexionEnCours: false,
      });
    } catch (erreur) {
      set({ connexionEnCours: false });
      throw erreur;
    }
  },

  async deconnecter() {
    try {
      await deballer(clientApi.POST('/v1/auth/deconnexion', {}));
    } catch {
      // Meme si l'API est injoignable, la session locale doit disparaitre : laisser des
      // jetons d'administration actifs dans un navigateur serait pire que l'echec.
    }

    viderJetons();
    set({ utilisateurId: null, email: null, estAdministrateur: null });
  },
}));
